import type { MailboxInfo } from "owa-client-types";
import type TableView from "../../store/schema/TableView";
import folderNameToId from "owa-session-store/lib/utils/folderNameToId";
import { groupRowKeysByMailboxInfo } from "./groupRowKeysByMailboxInfo";

/**
 * Returns true if a single mailbox can have the Archive triage action performed on it:
 * it must have an archive folder configured and not be a shared, archive, or group mailbox.
 */
function mailboxSupportsArchive(mailboxInfo: MailboxInfo): boolean {
    // If the mailbox has no archive folder, archiving is not supported.
    if (folderNameToId("archive", mailboxInfo) === "") {
        return false;
    }
    
    const type = mailboxInfo.type;
    return !(type === "SharedMailbox" || type === "ArchiveMailbox" || type === "GroupMailbox");
}

/**
 * For a (unified inbox) multi-select, returns true only if EVERY selected mailbox supports
 * the Archive action. Used to gate the Archive context menu item, ribbon button, and keyboard
 * shortcut so we only offer Archive when it can be applied to all selected mailboxes.
 *
 * Returns false when there is no selection or the table is in virtual select-all mode (the
 * selected mailboxes cannot be enumerated).
 *
 * @param defaultMailboxInfo the table's mailbox info (the synthetic UnifiedInbox mailbox in UIB)
 * @param tableView the table view holding the current selection
 */
export function doAllSelectedMailboxesSupportArchive(defaultMailboxInfo: MailboxInfo, tableView: TableView): boolean {
    if (tableView.isInVirtualSelectAllMode) {
        return false;
    }
    
    const rowKeys = [...tableView.selectedRowKeys.keys()];
    if (rowKeys.length === 0) {
        return false;
    }
    
    let groups;
    try {
        groups = groupRowKeysByMailboxInfo(defaultMailboxInfo, rowKeys, tableView);
    }
    catch {
        // If any selected row cannot be resolved to a mailbox, do not offer Archive.
        return false;
    }
    
    for (const { mailboxInfo } of groups.values()) {
        if (!mailboxSupportsArchive(mailboxInfo)) {
            return false;
        }
    }
    
    return true;
}
